import { default as React } from 'react';
import { makeStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  radioGroup: {
    flexDirection: 'row',
    gap: theme.spacing(4), // More space between radio buttons
  },
  radioLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.95rem',
      fontWeight: 500,
    },
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

export const RadioWidget = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const hasErrors = rawErrors && rawErrors.length > 0;

  // Get options from schema
  const options = (schema.enum || []) as string[];
  const enumNames = (uiSchema?.['ui:enumNames'] || options) as string[];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <FormControl
      fullWidth
      className={classes.root}
      error={hasErrors}
      required={required}
    >
      <FormLabel className={classes.label}>
        {schema.title || 'Radio Selection'}
      </FormLabel>

      <RadioGroup
        value={formData || ''}
        onChange={handleChange}
        className={classes.radioGroup}
      >
        {options.map((option: string, index: number) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio color="primary" />}
            label={enumNames[index] || option}
            className={classes.radioLabel}
          />
        ))}
      </RadioGroup>

      {hasErrors && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const RadioWidgetExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'RadioWidget',
    component: RadioWidget,
  }),
);
